"use client";

import { motion } from "motion/react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { type ReactNode, useEffect } from "react";

import AppLayoutTopNavigation from "@/components/app/app-layout/app-layout-top-navigation";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { IsLoggedIn } from "@/components/shared/is-logged-in";
import { Skeleton } from "@/components/ui/skeleton";
import { useLayout } from "@/contexts/layout-context";
import { SubscriptionTierHealthBootstrap } from "@/contexts/subscription-tier-health";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import useTradePageLayout from "@/hooks/use-trade-page-layout";
import { DIALOG } from "@/utils/dialog-routes";
import { isPWA } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { AppLayoutMain } from "./app-layout-main";
import AppLayoutMenu from "./app-layout-menu";
import { MainLayoutWrapper } from "./main-layout-wrapper";
import { RightSidebarWidgets } from "./right-sidebar-widgets";
import { RightSidebarWrapper } from "./right-sidebar-wrapper";
import { SearchDialogShortcutListener } from "./search-dialog-shortcut-listener";
import { SearchDialogTriggerInput } from "./search-dialog-trigger-input";

const TestnetBannerDynamic = dynamic(
  () => import("@/components/shared/banners/testnet-banner"),
  {
    ssr: false,
  },
);

const InvitesBannerDynamic = dynamic(
  () => import("@/components/shared/banners/invites-banner"),
  {
    ssr: false,
  },
);

const MobileMarketPricesLoading = () => {
  const pathname = usePathname();

  if (pathname !== "/") {
    return null;
  }

  return (
    <div
      className="
        unforce-radius grid h-9 grid-cols-2 items-center border-b
        border-border bg-background/95 px-4
        lg:hidden
      "
    >
      <div className="flex min-w-0 items-center justify-center gap-1.5">
        <Skeleton className="size-4 shrink-0 rounded-full" />
        <Skeleton className="h-3 w-[46px] rounded-sm" />
        <Skeleton className="h-3.5 w-[82px] rounded-sm" />
      </div>
      <div className="flex min-w-0 items-center justify-center gap-1.5">
        <Skeleton className="size-4 shrink-0 rounded-full" />
        <Skeleton className="h-3 w-[38px] rounded-sm" />
        <Skeleton className="h-3.5 w-[56px] rounded-sm" />
      </div>
    </div>
  );
};

const MobileMarketPricesDynamic = dynamic(
  () => import("@/components/app/app-layout/mobile-market-prices"),
  {
    loading: MobileMarketPricesLoading,
    ssr: false,
  },
);

interface AppLayoutProps {
  children: ReactNode;
}

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { pullToRefresh } = useLayout();
  const { isTradePage } = useTradePageLayout();
  const { motionProps, triggered, PullToRefreshIndicator } = usePullToRefresh({
    disabled: !pullToRefresh || !isPWA(),
  });

  useEffect(() => {
    if (triggered) {
      location.reload();
    }
  }, [triggered]);

  return (
    <>
      <SearchDialogShortcutListener />
      <SubscriptionTierHealthBootstrap />
      <motion.div
        className="relative min-h-safe-screen w-full"
        {...motionProps}
      >
        <TestnetBannerDynamic />
        <IsLoggedIn>
          <InvitesBannerDynamic />
        </IsLoggedIn>

        <div
          className={cn(
            `
              relative m-auto mb-(--mobile-nav-height) flex w-full flex-col
              justify-center
              lg:flex-row
            `,
            isTradePage ? "lg:max-w-[1340px]" : "lg:max-w-[1240px]",
          )}
        >
          <div
            className="
              unforce-radius relative hidden w-left-sidebar justify-start
              border-r
              lg:flex
            "
          >
            <div
              className="
                custom-scrollbar custom-scrollbar-hover-only sticky top-0
                h-safe-screen w-left-sidebar pr-[20px]
              "
            >
              <AppLayoutMenu />
            </div>
          </div>

          <MainLayoutWrapper>
            <AppLayoutTopNavigation />
            <MobileMarketPricesDynamic />
            <AppLayoutMain>{children}</AppLayoutMain>
          </MainLayoutWrapper>

          <RightSidebarWrapper>
            <div className="sticky top-0 z-20 py-4 pr-2 pl-4 backdrop-blur-md">
              <SearchDialogTriggerInput
                onOpen={() =>
                  setDialog(DIALOG.Search, {
                    open: true,
                  })
                }
              />
            </div>

            <div className="flex w-full flex-col gap-4 overflow-auto pr-2 pl-4">
              <RightSidebarWidgets />
            </div>
          </RightSidebarWrapper>
        </div>
      </motion.div>
      <PullToRefreshIndicator />
    </>
  );
};
